import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from 'react';
import cn from 'classnames';
import { Color } from '@type/index';
import { Tooltip } from '@components/ui/tooltip';
// import { useTranslation } from 'next-i18next';

interface Props {
  colors: Color[];
  className?: string;
  colorClassName?: string;
  setColor?: (color: Color) => void;
  colorName: boolean;
  colorNamePosition?: 'top' | 'bottom';
  onColorChange?: (color: Color) => void;
  paletteVariant?: 'sm' | 'md' | 'select' | 'xs' | 'lg';
  colorTextClassName?: string;
  maxCount?: number;
  value?: Color;
  label?: string;
  soldOutSlugs?: string[];
}

const ColorPalette = forwardRef<
  {
    color: string;
    name: string;
    setColor: (color: Color) => void;
  },
  Props
>(
  (
    {
      colors = [],
      className = '',
      colorClassName = '',
      colorName = false,
      colorNamePosition = 'top',
      onColorChange,
      paletteVariant = 'sm',
      colorTextClassName = '',
      maxCount,
      value,
      label = '',
      soldOutSlugs = [],
    },
    ref,
  ) => {
    const correctColors: Color[] =
      typeof colors === 'string' ? JSON.parse(colors) : colors;
    const [internalSelectedColor, setInternalSelectedColor] = useState<Color>(
      correctColors?.[0],
    );

    const selectedColor = value !== undefined ? value : internalSelectedColor;

    // const { t } = useTranslation('common');

    useImperativeHandle(ref, () => ({
      color: selectedColor?.colorCode,
      name: selectedColor?.colorName,
      setColor: setInternalSelectedColor,
    }));

    const handleColorClick = useCallback(
      (color: Color) => {
        if (value === undefined) {
          setInternalSelectedColor(color);
        }
        if (onColorChange) {
          onColorChange(color);
        }
      },
      [onColorChange, value],
    );

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedColorCode = e.target.value;
      const color = correctColors.find(
        (c: Color) => c.colorCode === selectedColorCode,
      );
      if (color) {
        handleColorClick(color);
      }
    };

    const palette = useMemo(() => {
      if (Array.isArray(correctColors) && correctColors.length > 0) {
        const visibleColors =
          typeof maxCount === 'number'
            ? correctColors.slice(0, maxCount)
            : correctColors;
        const hiddenColors =
          typeof maxCount === 'number' ? correctColors.slice(maxCount) : [];

        const isSelected = (color: Color) => {
          if (selectedColor?.slug && color?.slug) {
            return selectedColor.slug === color.slug;
          }
          return selectedColor?.colorName === color?.colorName;
        };

        return (
          <>
            {visibleColors.map((color, index) => {
              const isSoldOut = soldOutSlugs?.includes(color.slug ?? '');
              return (
                <div
                  key={index}
                  className={cn(
                    'rounded-full transition-all duration-150 ease-linear flex items-center justify-center relative overflow-hidden',
                    isSoldOut ? 'cursor-pointer border border-red-500' : 'cursor-pointer',
                    paletteVariant === 'lg'
                      ? 'w-8 h-8'
                      : paletteVariant === 'sm'
                        ? 'w-6 h-6'
                        : paletteVariant === 'xs'
                          ? 'w-5 h-5'
                          : 'w-5 h-5',
                    colorClassName,
                  )}
                  style={
                    isSelected(color)
                      ? {
                          borderColor: color?.colorCode,
                          borderWidth: '1px',
                          borderStyle: 'solid',
                        }
                      : {}
                  }
                  onClick={() => handleColorClick(color)}
                >
                  <div
                    className="rounded-full"
                    style={{
                      backgroundColor: color?.colorCode,
                      width: isSelected(color) ? 'calc(100% - 4px)' : '100%',
                      height: isSelected(color) ? 'calc(100% - 4px)' : '100%',
                    }}
                  />
                  {isSoldOut && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-full h-[1.5px] bg-red-500 rotate-45" />
                    </div>
                  )}
                </div>
              );
            })}
            {hiddenColors.length > 0 && (
              <Tooltip
                content={hiddenColors
                  .map((color) => color.colorName)
                  .join(', ')}
              >
                <div className="flex items-center justify-center text-xs text-gray-500 cursor-pointer hover:underline">
                  +{hiddenColors.length} more ...
                </div>
              </Tooltip>
            )}
          </>
        );
      } else {
        return null;
      }
    }, [
      correctColors,
      selectedColor,
      paletteVariant,
      colorClassName,
      handleColorClick,
      maxCount,
    ]);

    if (correctColors?.length === 0) {
      return null;
    }

    // Select variant
    if (paletteVariant === 'select') {
      return (
        <div className={cn('relative', className)}>
          <select
            value={selectedColor?.colorCode || ''}
            onChange={handleSelectChange}
            className="w-full appearance-none bg-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-black
              overflow-hidden text-ellipsis pr-14 whitespace-nowrap
            "
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
            }}
          >
            {(correctColors || []).map((color, index) => (
              <option key={index} value={color.colorCode}>
                {color.colorName}
              </option>
            ))}
          </select>
          {/* Selected color dot indicator */}

          <div
            className={cn(
              'absolute right-8 top-1/2 -translate-y-1/2 rounded-full cursor-pointer transition-all duration-150 ease-linear flex items-center justify-center h-4 w-4',
            )}
            style={{
              borderColor: selectedColor?.colorCode,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <div
              className="rounded-full"
              style={{
                backgroundColor: selectedColor?.colorCode,
                width: 'calc(100% - 4px)',
                height: 'calc(100% - 4px)',
              }}
            />
          </div>
        </div>
      );
    }

    const colorNameElement = (
      <span className={cn('text-xs', colorTextClassName)}>
        {label ? <span className="text-[#666666]">{label}</span> : null}{' '}
        <span className="text-blackText">{selectedColor?.colorName}</span>
      </span>
    );

    if (colorName) {
      return (
        <div
          className="flex p-1 flex-col"
          style={label ? { gap: '12px' } : { gap: '8px' }}
        >
          {colorNamePosition === 'top' && colorNameElement}
          <div className={cn('flex gap-2 flex-wrap', className)}>{palette}</div>
          {colorNamePosition === 'bottom' && colorNameElement}
        </div>
      );
    }

    return (
      <div className={cn('flex p-1 gap-2 h-max', className)}>{palette}</div>
    );
  },
);

export default ColorPalette;
